import { pdf } from '@react-pdf/renderer';
import { useMelwaysLink } from '@seek/melways-react';
import {
  Loader,
  Alert,
  Text,
  Stack,
  IconPrint,
  IconDownload,
} from 'braid-design-system';
import { saveAs } from 'file-saver';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useReactToPrint } from 'react-to-print';

import { StaticPageProvider } from 'src/components';
import { ButtonTracking } from 'src/components/ButtonTracking/ButtonTracking';
import { useConfig, useEnvironment } from 'src/shared/hooks';
import type { PageProps, ConfigContextTypes } from 'src/shared/types';

import getJobDetails from '../../pages/products/QRCode/graphQL/getJobDetails';

import QRCodepage from './components/QRCodePage/QRCodePage';
import QRCodePdf from './components/QRCodePdf/QRCodePdf';

import {
  StyledButtonContainer,
  StyledContainerWrapper,
  StyledPrintButtonContainer,
  StyledDownloadButtonContainer,
} from './components/QRCodePage.css';

interface QRCodeProps {
  qrCodeValue: string;
  jobTitle: string;
  qrCodeId: string;
  jobPostedDate: string;
}

interface JobDetails {
  id: string;
  title: string;
  createdAt: { dateTimeUtc: string };
  isExpired: boolean;
}

export const QrcodeContainer = ({ jobAdId }: { jobAdId: string }) => {
  const config = useConfig();
  const trackingProps = {
    page: 'qr code',
    eventName: 'qr_code_displayed',
    siteSection: 'products',
    currentPage: 'qr code',
    subSection: 'qr-code',
    linkName: 'qr_code_displayed',
    jobId: jobAdId,
  };

  const StaticPageProps: PageProps = {
    title: 'QR code',
    returnUrl: '/products/qr-code',
    activeTab: 'Products',
    activeSubTab: 'QR code',
    ...trackingProps,
  };

  const componentRef = React.useRef(null);

  const [jobDetails, setJobDetails] = useState<JobDetails>();
  const [isClient, setIsClient] = useState(false);
  const [myDocument, setMyDocument] = useState<Document | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const environment = useEnvironment();
  const localize = useMelwaysLink();

  useEffect(() => {
    setIsClient(true);
    setMyDocument(document);
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await getJobDetails(jobAdId);
      if (data && data.jobDetails) {
        const { job } = data.jobDetails;
        setLoading(false);
        setJobDetails(job);
      } else {
        setLoading(false);
        setError(true);
      }
    })();
  }, [jobAdId, environment]);

  const generateQRCodePageProps = (
    { id, title, createdAt: { dateTimeUtc } }: JobDetails,
    configContext: ConfigContextTypes,
  ): QRCodeProps => {
    const jobUrl = localize({
      absolute: true,
      product: 'candidate',
      country: configContext.country,
      path: `/job/${id}?tracking=qr`,
      language: 'en',
    });

    return {
      qrCodeId: id,
      jobTitle: title,
      jobPostedDate: moment(dateTimeUtc).format('DD/MM/YYYY'),
      qrCodeValue: jobUrl,
    };
  };

  const renderInvalidJobErrorJSX = (
    <Alert tone="critical">
      <Text>
        Something went wrong, we can’t create your QR code poster right now.
      </Text>
    </Alert>
  );

  const renderExpiredJobErrorJSX = (
    <Alert tone="critical">
      <Text>This job ad is no longer advertised.</Text>
    </Alert>
  );

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const renderQRCodeJSX = (jobDetails: JobDetails) => (
    <div className={StyledContainerWrapper}>
      <QRCodepage
        ref={componentRef}
        {...generateQRCodePageProps(jobDetails, config)}
      />
      <div className={StyledButtonContainer}>
        <div className={StyledPrintButtonContainer}>
          <ButtonTracking
            tracking="qr_code_print_pressed"
            variant="solid"
            onClick={handlePrint}
            size="small"
            icon={<IconPrint />}
          >
            Print
          </ButtonTracking>
        </div>
        <div className={StyledDownloadButtonContainer}>
          <ButtonTracking
            tracking="qr_code_download_pressed"
            variant="solid"
            size="small"
            icon={<IconDownload />}
            onClick={async () => {
              const documentToBeDownloaded = (
                <QRCodePdf
                  document={myDocument!}
                  {...generateQRCodePageProps(jobDetails, config)}
                />
              );
              const asPdf = pdf(documentToBeDownloaded);
              asPdf.updateContainer(documentToBeDownloaded);
              const blob = await asPdf.toBlob();
              saveAs(blob, `QRCode_${jobDetails.title}_${jobDetails.id}.pdf`);
            }}
          >
            Download
          </ButtonTracking>
        </div>
      </div>
    </div>
  );

  const reactToPrintContent = React.useCallback(() => componentRef.current, []);

  const handlePrint = useReactToPrint({
    content: reactToPrintContent,
    documentTitle: 'QRCode',
    removeAfterPrint: true,
  });
  return (
    <StaticPageProvider {...StaticPageProps}>
      <Stack space="xxsmall" align="center">
        {loading && <Loader />}
        {error && renderInvalidJobErrorJSX}
        {jobDetails?.isExpired && renderExpiredJobErrorJSX}
        {isClient &&
          jobDetails &&
          !jobDetails?.isExpired &&
          renderQRCodeJSX(jobDetails)}
      </Stack>
    </StaticPageProvider>
  );
};
